import { ColumnsType } from 'antd/es/table';
import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ServerTable } from 'shared/ui/ServerTable';
import { ServerTableActions, ServerTableRowActions } from 'shared/ui/ServerTable/types';
import { ReactComponent as FiltersIcon } from 'shared/assets/icons/FiltersIcon.svg';
import { ReactComponent as ContractsTableIcon } from 'shared/assets/icons/ContractsTableIcon.svg';
import { Modal } from 'shared/ui/Modal';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppDispatch } from 'app/config/storeConfig/hooks';
import { AppRoutes } from 'app/config/routerConfig/types';
import { ExportModal } from 'features/ExportModal';
import { useTableFilterContext } from 'features/TableFilter';
import { Zone, useGetPaginatedZonesQuery, useDeleteZoneMutation, ZoneObjectType } from 'entities/Zone';
import { ZonesListFilter } from './ZonesListFilter';
import { transformZonesListFilters } from '../utils/transformZonesListFilters';
import classNames from 'classnames';
import { createZoneModalActions } from 'widgets/CreateZoneModal';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { useGlobalConfirmModal } from 'app/globalConfirmModal';
import { showNotification } from 'app/providers/NotificationsProvider';

import { DrawerMode, ZoneFormDrawer, zoneFormDrawerActions } from 'features/ZoneFormDrawer';
import { ReactComponent as PlusIcon } from 'shared/assets/icons/PlusIcon.svg';
import { ReactComponent as EditIcon } from 'shared/assets/icons/EditIcon.svg';
import { ReactComponent as DeleteIcon } from 'shared/assets/icons/DeleteIcon.svg';

export const ZonesTable: FC = memo(() => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { warehouseId } = useParams();
  const { openModal, closeModal } = useGlobalConfirmModal();
  const [deleteZoneApiRequest, { isLoading: isZoneDeleting }] = useDeleteZoneMutation();
  const { t } = useAppTranslation(['common', 'user', 'warehouses']);

  const [usersModalOpen, setUsersModalOpen] = useState(false);
  const [objectsModalOpen, setObjectsModalOpen] = useState(false);
  const [modalZoneUsers, setModalZoneUsers] = useState<Array<{ userId: string; email?: string; firstName?: string; lastName?: string }>>(
    [],
  );
  const [modalZoneObjects, setModalZoneObjects] = useState<
    Array<{ objectType: ZoneObjectType.BOX | ZoneObjectType.DOOR; objectId: string; name?: string }>
  >([]);

  const { setFiltersOpened, appliedFilters, tags, clearAllFilters } = useTableFilterContext();

  const openCreateZoneModal = useCallback((): void => {
    dispatch(createZoneModalActions.setOpened(true));
  }, [dispatch]);

  const editZone = useCallback(
    (zone: Zone) => {
      dispatch(zoneFormDrawerActions.setOpenZoneForm({ mode: DrawerMode.ZONE_EDIT, zone }));
    },
    [dispatch],
  );

  const handleDeleteZone = useCallback(
    async (zone: Zone): Promise<void> => {
      try {
        if (zone && warehouseId) {
          await deleteZoneApiRequest({ warehouseId, zoneId: zone.zoneId }).unwrap();

          showNotification('info', t('Success', { ns: 'common' }), t('Zone was deleted successfully'));
        }

        closeModal();
      } catch (error) {
        showNotification('error', t('Error', { ns: 'common' }), t('Error when deleting zone'));
        console.log('error', error);
      }
    },
    [closeModal, deleteZoneApiRequest, t],
  );

  const deleteZone = useCallback(
    (zone: Zone) => {
      openModal({
        title: t('Delete zone'),
        description: t('Are you sure you want to delete this zone?'),
        onCancel: closeModal,
        onOk: async () => {
          await handleDeleteZone(zone);
        },
        isConfirmDisabled: isZoneDeleting,
      });
    },
    [closeModal, handleDeleteZone, openModal, t],
  );

  const openUsersModal = useCallback((users: typeof modalZoneUsers) => {
    setModalZoneUsers(users || []);
    setUsersModalOpen(true);
  }, []);

  const openObjectsModal = useCallback((objects: typeof modalZoneObjects) => {
    setModalZoneObjects(objects || []);
    setObjectsModalOpen(true);
  }, []);

  const closeUsersModal = useCallback((): void => {
    setUsersModalOpen(false);
  }, []);

  const closeObjectsModal = useCallback((): void => {
    setObjectsModalOpen(false);
  }, []);

  const goToUserContracts = useCallback(
    (userId: string) => {
      navigate(`${AppRoutes.USERS}/${userId}/contracts`);
    },
    [navigate],
  );

  const userLabel = (user: { userId: string; firstName?: string; lastName?: string; email?: string }): string =>
    [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || '—';

  const objectLabel = (object: { objectType: ZoneObjectType.BOX | ZoneObjectType.DOOR; objectId: string; name?: string }): string => {
    if (object.name) {
      return object.objectType === ZoneObjectType.DOOR ? getLocalizedString(object.name) : object.name;
    }

    const short = object.objectId?.slice(0, 8);

    return object.objectType === ZoneObjectType.BOX ? `Box #${short}` : `Door #${short}`;
  };

  const columns = useMemo<ColumnsType<Zone>>(
    () => [
      {
        title: t('Zone name'),
        key: 'name',

        render: (_, record) => <div className="text-primary font-semibold">{record.name}</div>,
      },
      {
        title: t('Warehouse'),
        key: 'warehouse',

        render: (_, record) => <div className="text-primary">{record.warehouse?.name}</div>,
      },
      {
        title: t('Users', { ns: 'user' }),
        key: 'users',

        render: (_, record) => {
          const users = record.users || [];
          if (!users.length) {
            return <span className="text-secondary">—</span>;
          }

          const visible = users.slice(0, 2);
          const rest = users.length - visible.length;

          return (
            <div className="flex items-center gap-2 flex-wrap">
              {visible.map((user) => (
                <span key={user.userId} className="px-2 py-1 rounded bg-secondary text-primary text-xs">
                  {userLabel(user)}
                </span>
              ))}
              {rest > 0 && (
                <button
                  className="text-accent underline text-xs"
                  onClick={() => {
                    openUsersModal(users);
                  }}
                >
                  {t('Show all')} ({users.length})
                </button>
              )}
            </div>
          );
        },
      },
      {
        title: t('Zone objects'),
        key: 'objects',

        render: (_, record) => {
          const objects = record.objects || [];
          if (!objects.length) {
            return <span className="text-secondary">—</span>;
          }

          const visible = objects.slice(0, 2);
          const rest = objects.length - visible.length;

          return (
            <div className="flex items-center gap-2 flex-wrap">
              {visible.map((object) => (
                <span key={object.objectId} className="px-2 py-1 rounded bg-secondary text-primary text-xs">
                  {objectLabel(object)}
                </span>
              ))}
              {rest > 0 && (
                <button
                  className="text-accent underline text-xs"
                  onClick={() => {
                    openObjectsModal(objects);
                  }}
                >
                  {t('Show all')} ({objects.length})
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [t, openObjectsModal, openUsersModal],
  );

  const tableActions: Array<ServerTableActions<Zone>> = useMemo(
    () => [
      {
        name: t('Create Zone', { ns: 'warehouses' }),
        icon: <PlusIcon />,
        iconPosition: 'prev',
        onClick: openCreateZoneModal,
      },
      {
        name: t('Filters', { ns: 'common' }),
        icon: <FiltersIcon />,
        theme: 'secondary',
        iconPosition: 'prev',
        onClick: () => {
          setFiltersOpened(true);
        },
      },
    ],
    [setFiltersOpened, t, openCreateZoneModal],
  );

  const highlightRow = (record: Zone): string => {
    return classNames({
      'bg-secondary': !record.users?.length,
    });
  };

  const rowActions: Array<ServerTableRowActions<Zone>> = useMemo(
    () => [
      {
        name: 'editZone',
        icon: <EditIcon />,
        theme: 'clear',
        description: t('Edit zone'),
        onClick: editZone,
      },
      {
        name: 'deleteZone',
        icon: <DeleteIcon />,
        theme: 'clear',
        description: t('Delete zone'),
        onClick: deleteZone,
      },
    ],
    [editZone, deleteZone, t],
  );

  return (
    <div className="pt-4">
      <div className="font-semibold text-3xl mb-7 font-tektur">{t('Zones')}</div>
      <ServerTable
        rowActions={rowActions}
        columns={columns}
        fetchData={useGetPaginatedZonesQuery}
        rowKey="zoneId"
        tableActions={tableActions}
        tags={tags}
        search
        tableActionsPosition="prev"
        searchPlaceholder={t('Enter zone name or warehouse')}
        defaultFilters={{ ...transformZonesListFilters(appliedFilters), warehouseId }}
        onAllFiltersClear={clearAllFilters}
        rowClassName={highlightRow}
      />
      <ExportModal />
      <ZonesListFilter />
      <ZoneFormDrawer />

      <Modal isOpen={usersModalOpen} onClose={closeUsersModal} width={560}>
        <div className="mb-4 font-semibold text-lg">{t('Zone users')}</div>
        <div className="space-y-3">
          {modalZoneUsers.map((user) => (
            <div key={user.userId} className="flex items-center justify-between p-2 rounded bg-secondary">
              <div className="flex flex-col">
                <span className="text-primary font-medium">{userLabel(user)}</span>
                <span className="text-primaryLight text-xs">{user.email || '—'}</span>
              </div>
              <button
                className="flex items-center gap-1 text-accent underline text-sm"
                onClick={() => {
                  goToUserContracts(user.userId);
                }}
              >
                {t('Contracts')}
                <ContractsTableIcon />
              </button>
            </div>
          ))}
        </div>
      </Modal>

      <Modal isOpen={objectsModalOpen} onClose={closeObjectsModal} width={560}>
        <div className="mb-4 font-semibold text-lg">{t('Zone objects')}</div>
        <div className="flex flex-wrap gap-2">
          {modalZoneObjects.map((object) => (
            <div key={object.objectId} className="flex items-center justify-between px-3 py-2 rounded-lg shadow-sm bg-secondary">
              <div className="flex flex-col">
                <span className="text-primary font-medium">{objectLabel(object)}</span>
                <span className="text-primaryLight text-xs">{t(object.objectType).toUpperCase()}</span>
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
});
